import { useLazyQuery } from '@apollo/client';
import { DECODE_VIN_VALUES } from '../api';
import { useNotifications } from 'modules/notification';
import {
  getEngineTypeFromVinDecoding,
  getTorqueByEngineType,
  getTrnsactClassValueFromVinDecoding,
  getTrnsactMakeValueFromVinDecoding,
} from '../lib';
import { FACT_NAMES_STRING } from '@trnsact/business-criteria';
import { useFormContext } from 'react-hook-form';
import { FinanceProgramFormValues } from '../types';
import { useSelector } from 'react-redux';
import { deskingSelectors } from '../model';

export function useSearchByVin() {
  const [getDecodedVinQuery, { loading: isLoading }] = useLazyQuery(DECODE_VIN_VALUES);

  const { showNotification } = useNotifications();
  const { setValue } = useFormContext<FinanceProgramFormValues>();
  const uniqProductsFactsToCheck = useSelector(deskingSelectors.selectUniqProductsFactsToCheck);

  const getDecodedVin = async (vin: string) => {
    const response = await getDecodedVinQuery({ variables: { vin } });
    const equipmentInfo = response.data?.decodeVinValues?.Results?.[0];

    if (equipmentInfo?.ErrorCode !== '0') {
      showNotification('Could not decode VIN', { type: 'info' });
      return null;
    }

    return equipmentInfo;
  };

  const setEquipmentInfoFromDecodedValues = (
    decodedEquipmentInfo: any,
    onChangeAllProductsConfig?: (configField: string, nextValue: string | number) => void
  ) => {
    if (!decodedEquipmentInfo) {
      return;
    }

    setValue('equipment.model', decodedEquipmentInfo.Model, { shouldDirty: true });
    setValue('equipment.year', decodedEquipmentInfo.ModelYear, { shouldDirty: true });
    setValue('equipment.make', getTrnsactMakeValueFromVinDecoding(decodedEquipmentInfo.Make), { shouldDirty: true });
    setValue('equipment.class', getTrnsactClassValueFromVinDecoding(decodedEquipmentInfo.GVWR), { shouldDirty: true });

    if (onChangeAllProductsConfig) {
      onChangeAllProductsConfig(
        FACT_NAMES_STRING.EQUIPMENT_CLASS,
        getTrnsactClassValueFromVinDecoding(decodedEquipmentInfo.GVWR)
      );

      const isNeedToSetEngineType = uniqProductsFactsToCheck.some(
        fact => fact === FACT_NAMES_STRING.EQUIPMENT_ENGINE_TYPE
      );
      const isNeedToSetEngineTorque = uniqProductsFactsToCheck.some(
        fact => fact === FACT_NAMES_STRING.ENGINE_TORQUE_MODE
      );
      const [fullEngineType, shortEngineType] = getEngineTypeFromVinDecoding(decodedEquipmentInfo.EngineModel);
      if (fullEngineType && shortEngineType) {
        isNeedToSetEngineType && onChangeAllProductsConfig(FACT_NAMES_STRING.EQUIPMENT_ENGINE_TYPE, shortEngineType);
        // Comment for now because "I suspect we will add this functionality back at some point in the future" (from DR-8477 ticket)
        /*         isNeedToSetEngineTorque &&
          onChangeAllProductsConfig(FACT_NAMES_STRING.ENGINE_TORQUE_MODE, getTorqueByEngineType(fullEngineType)); */
      }
    }
  };

  return {
    isLoading,
    getDecodedVin,
    setEquipmentInfoFromDecodedValues,
  };
}
